import { Component, OnDestroy, OnInit } from '@angular/core';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { DynamicSkillsService } from 'src/app/_services/admin/dynamic-skills.service';
import { UsersService } from 'src/app/_services/admin/users.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { DeleteRecordService } from 'src/app/_services/delete-record.service';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-user-manage-skills',
  standalone: false,
  templateUrl: './user-manage-skills.component.html',
  styleUrl: './user-manage-skills.component.scss'
})
export class UserManageSkillsComponent extends SubscriptionUtil implements OnInit, OnDestroy {
  translate: any;
  data: any;
  newSkillName: string = '';
  newSkillColor: string = '#8BC98B';
  createSkillSubmitted: boolean = false;
  creatingSkill: boolean = false;
  editingSkillId: number | null = null;
  editSkillName: string = '';
  editSkillColor: string = '#8BC98B';
  editSkillSubmitted: boolean = false;
  updatingSkill: boolean = false;
  skillLibraryList: any[] = [];
  selectedSkillId: any = null;
  selectedSkills: any[] = [];
  activeTabIndex: number = 0;
  saving: boolean = false;
  saved = false;
  canAssignSkills = false;

  private skillChipPalette: { color: string; background: string }[] = [
    { color: '#3B8B3B', background: '#DDF3DD' },
    { color: '#1E88A8', background: '#D9F0F5' },
    { color: '#C23B8E', background: '#FBE0F0' },
    { color: '#2E9BB0', background: '#DCF3F6' },
    { color: '#6C63C7', background: '#E4E1F9' },
  ];

  constructor(
    private languageTranslateService: LanguageTranslateService,
    public modalRef: BsModalRef,
    private skillsService: DynamicSkillsService,
    private userService: UsersService,
    private errorUtil: ErrorUtil,
    private deleteRecordService: DeleteRecordService,
    private perm: PermissionService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.canAssignSkills = !!this.data?.skipUserSave || this.perm.capable('manage_users', 'read');
    this.getLanguageData();
    this.prefillAssignedSkills();
    this.push(
      this.skillsService.refreshList$.subscribe(() => {
        this.loadSkillLibrary();
      })
    );
  }

  private prefillAssignedSkills() {
    const existing = this.data?.userDynamicSkillList || this.data?.skills || [];
    if (Array.isArray(existing) && existing.length) {
      this.selectedSkills = existing.map((skill: any, index: number) => this.mapSkillChip(skill, index));
    }
  }

  loadSkillLibrary() {
    this.push(
      this.skillsService.getSkillLibrary(1, 1000, 'name', 'asc').subscribe((res: any) => {
        this.skillLibraryList = Array.isArray(res)
          ? res
          : (res?.data || res?.content || []);
      })
    );
  }

  get availableSkillOptions(): any[] {
    const selectedIds = new Set(this.selectedSkills.map((s) => s.id));
    return this.skillLibraryList.filter((skill) => !selectedIds.has(skill.id));
  }

  onSkillSelect(event: any) {
    const skillId = event?.value;
    if (skillId == null) {
      return;
    }

    const alreadySelected = this.selectedSkills.some((s) => s.id === skillId);
    if (alreadySelected) {
      this.selectedSkillId = null;
      return;
    }

    const skill = this.skillLibraryList.find((s) => s.id === skillId);
    if (!skill) {
      this.selectedSkillId = null;
      return;
    }

    this.selectedSkills = [
      ...this.selectedSkills,
      this.mapSkillChip(skill, this.selectedSkills.length),
    ];
    this.selectedSkillId = null;
  }

  removeSelectedSkill(index: number) {
    this.selectedSkills = this.selectedSkills.filter((_, i) => i !== index);
  }

  onCreateSkill() {
    this.createSkillSubmitted = true;
    const name = (this.newSkillName || '').trim();
    if (!name) {
      return;
    }

    const colorCode = this.newSkillColor || '#8BC98B';
    const body = {
      name,
      label: name,
      colorCode,
      isDefault: false,
      systemValue: this.toSystemValue(name),
    };

    this.creatingSkill = true;
    this.push(
      this.skillsService.createSkillLibrary(body).subscribe((res: any) => {
        this.creatingSkill = false;
        if (res?.status === 200 || res?.status === 201) {
          this.errorUtil.setErrorMessage(
            200,
            this.translate?.general.messages.savedSuccessfully,
            null,
            'success',
            1000
          );
          this.newSkillName = '';
          this.createSkillSubmitted = false;
          this.loadSkillLibrary();
        } else {
          this.errorUtil.setErrorMessage(
            res?.status || 400,
            null,
            res?.error?.title || res?.error?.message || null,
            'error',
            3000
          );
        }
      })
    );
  }

  onEditSkill(skill: any) {
    if (!skill?.id) {
      return;
    }
    this.editingSkillId = skill.id;
    this.editSkillName = skill?.label || skill?.name || '';
    this.editSkillColor = skill?.colorCode || '#8BC98B';
    this.editSkillSubmitted = false;
  }

  cancelEditSkill() {
    this.editingSkillId = null;
    this.editSkillName = '';
    this.editSkillColor = '#8BC98B';
    this.editSkillSubmitted = false;
  }

  onDeleteSkill(skill: any) {
    if (!skill?.id) {
      return;
    }

    this.push(
      this.deleteRecordService.getItems({
        deleteType: 'Delete Skill',
        deleteHeader: 'Delete Skill Confirmation',
        displayDeleteTitle: true,
        message: 'Are you sure you want to delete this skill?',
        id: skill.id,
      }).subscribe((res: any) => {
        if (res?.status === 200 || res?.status === 204) {
          this.selectedSkills = this.selectedSkills.filter((s) => s.id !== skill.id);
          if (this.editingSkillId === skill.id) {
            this.cancelEditSkill();
          }
        }
      })
    );
  }

  onUpdateSkill(skill: any) {
    this.editSkillSubmitted = true;
    const name = (this.editSkillName || '').trim();
    if (!name || !skill?.id) {
      return;
    }

    const body = {
      id: skill.id,
      name,
      label: name,
      colorCode: this.editSkillColor || skill?.colorCode || '#8BC98B',
      isDefault: skill?.isDefault ?? false,
      systemValue: this.toSystemValue(name),
    };

    this.updatingSkill = true;
    this.push(
      this.skillsService.updateSkillLibrary(body).subscribe((res: any) => {
        this.updatingSkill = false;
        if (res?.status === 200 || res?.status === 201 || res?.status === 204) {
          this.errorUtil.setErrorMessage(
            200,
            this.translate?.general.messages.savedSuccessfully,
            null,
            'success',
            1000
          );
          this.cancelEditSkill();
          this.loadSkillLibrary();
        } else {
          this.errorUtil.setErrorMessage(
            res?.status || 400,
            null,
            res?.error?.title || res?.error?.message || null,
            'error',
            3000
          );
        }
      })
    );
  }

  private toSystemValue(name: string): string {
    return name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  onSave() {
    if (this.saving) {
      return;
    }

    if (this.data?.skipUserSave) {
      this.saved = true;
      this.modalRef.hide();
      return;
    }

    if (!this.selectedSkills?.length) {
      return;
    }

    const userId = this.data?.id;
    if (!userId) {
      this.errorUtil.setErrorMessage(400, null, 'User id is missing', 'error', 3000);
      return;
    }

    const body = {
      assignedSkillLibraryIds: this.selectedSkills.map((s) => s.id),
    };

    this.saving = true;
    this.push(
      this.userService.saveUserSkills(userId, body).subscribe((res: any) => {
        this.saving = false;
        if (res?.status === 200 || res?.status === 201 || res?.status === 204) {
          this.errorUtil.setErrorMessage(
            200,
            this.translate?.general.messages.savedSuccessfully,
            null,
            'success',
            1000
          );
          this.modalRef.hide();
        } else {
          this.errorUtil.setErrorMessage(
            res?.status || 400,
            null,
            res?.error?.title || res?.error?.message || null,
            'error',
            3000
          );
        }
      })
    );
  }

  private mapSkillChip(skill: any, index: number) {
    const palette = this.skillChipPalette[index % this.skillChipPalette.length];
    const background =skill?.colorCode || palette.background;
    const color =
      skill?.textColor ||
      (skill?.color && skill.color !== background ? skill.color : null) ||
      this.darkenHexColor(background, 0.45) ||
      palette.color;

    return {
      id: skill?.id ?? skill?.skillLibraryId ?? skill?.skillId,
      name: skill?.name || skill?.label || skill,
      label: skill?.label || skill?.name || skill,
      color,
      background,
    };
  }

  getSkillChipStyles(skill: any, index: number = 0) {
    const mapped = this.mapSkillChip(skill, index);
    return {
      color: mapped.color,
      'background-color': mapped.background,
    };
  }

  private darkenHexColor(hex: string, amount: number = 0.4): string {
    if (!hex || typeof hex !== 'string') {
      return '#3B8B3B';
    }

    let cleaned = hex.trim().replace('#', '');
    if (cleaned.length === 3) {
      cleaned = cleaned.split('').map((c) => c + c).join('');
    }
    if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) {
      return '#3B8B3B';
    }

    const num = parseInt(cleaned, 16);
    let r = (num >> 16) & 255;
    let g = (num >> 8) & 255;
    let b = num & 255;

    r = Math.max(0, Math.floor(r * (1 - amount)));
    g = Math.max(0, Math.floor(g * (1 - amount)));
    b = Math.max(0, Math.floor(b * (1 - amount)));

    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
  }

  /********************************** Start Language **************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
          this.loadSkillLibrary();
        }
      })
    );
  }
  /********************************** End Language **************************/

  push(obs: any) {
    super.push(obs);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
